/*
  7Raw search: <r7-search> (01 §2.6, §6.5, A01-5, A01-6; 08 §6.9; 00 §6.3, A00-6).
  Markup: snippets/7r-search-panel.liquid. Index: templates/collection.7r-search-index.liquid.

  Matching reproduces the prototype (L1217–1219): the whole trimmed query, lower-cased and without
  diacritics, is a substring test against one haystack per entry. No tokenising, no fuzziness.
    - Scents: name, style, notes, short description, top / heart / base notes.
    - Products: their scent's fields, custom.format, the locale format labels, the title, and the
      set terms for the product picked in settings.r7_search_set_product.
  Products are ordered by scent (index order: dots 1 → 4, then 0; unscented last), then by the
  header's format-block order, then by collection order.

  Element API (used by r7-header):
    open attribute       wide panel shown / hidden (CSS); the index is fetched on first open
    focusInput()         focus the field 90 ms later (prototype L1048)
    clear()              empty the field and reset the results
    'r7:search-close'    bubbling event, detail.reason 'close' | 'escape' | 'navigate'
*/
(() => {
  const normalize = (value) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  const scentText = (scent) =>
    scent ? [scent.name, scent.style, scent.notes, scent.desc, scent.top, scent.heart, scent.base].join(' ') : '';

  /** Builds the searchable haystacks and the result order once per index. */
  function prepare(index, { formatOrder = [], normalize: norm = normalize } = {}) {
    const scents = Array.isArray(index?.scents) ? index.scents : [];
    const products = Array.isArray(index?.products) ? index.products : [];
    const set = index?.set ?? {};
    const scentByHandle = new Map(scents.map((scent) => [scent.handle, scent]));
    const scentRank = new Map(scents.map((scent, position) => [scent.handle, position]));
    const rankOfScent = (handle) => (scentRank.has(handle) ? scentRank.get(handle) : scents.length);
    const rankOfFormat = (format) => {
      const position = formatOrder.indexOf(format);
      return position === -1 ? formatOrder.length : position;
    };

    return {
      normalize: norm,
      scents: scents.map((scent) => ({ item: scent, haystack: norm(scentText(scent)) })),
      products: products
        .map((product, position) => ({ product, position }))
        .sort(
          (a, b) =>
            rankOfScent(a.product.scent) - rankOfScent(b.product.scent) ||
            rankOfFormat(a.product.format) - rankOfFormat(b.product.format) ||
            a.position - b.position
        )
        .map(({ product }) => {
          const scent = scentByHandle.get(product.scent) ?? null;
          const setTerms = set.handle && product.handle === set.handle ? set.terms : '';
          return {
            item: { ...product, name: scent ? scent.name : product.title },
            haystack: norm([scentText(scent), product.format, product.labels, product.title, setTerms].join(' ')),
          };
        }),
    };
  }

  /** Returns null for an empty query, otherwise the matching scents and products in index order. */
  function search(prepared, query) {
    const needle = prepared.normalize(String(query ?? '').trim());
    if (!needle) return null;
    const hits = (entries) => entries.filter((entry) => entry.haystack.includes(needle)).map((entry) => entry.item);
    return { scents: hits(prepared.scents), products: hits(prepared.products) };
  }

  /** Pure matcher: match(index, query, { formatOrder, normalize }) → null | { scents, products }. */
  function match(index, query, options) {
    return search(prepare(index, options), query);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { match, prepare, search, normalize };
  }

  if (typeof customElements === 'undefined' || customElements.get('r7-search')) return;

  const FOCUS_DELAY = 90;
  const PAGES_DELAY = 200;
  const ANNOUNCE_DELAY = 500;
  const PAGES_LIMIT = 4;
  const COUNT_TOKEN = '9999';

  const indexRequests = new Map();
  const loadIndex = (url) => {
    if (!indexRequests.has(url)) {
      // No `Accept: application/json` here: Shopify routes JSON-accepting requests to its own
      // collection JSON endpoint, which ignores `?view=` and answers 404.
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Search index request failed: ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          indexRequests.delete(url);
          throw error;
        });
      indexRequests.set(url, request);
    }
    return indexRequests.get(url);
  };

  const pagesFrom = (data) => {
    const results = data?.resources?.results ?? {};
    const pages = (results.pages ?? []).map((page) => ({ title: page.title, url: page.url, kind: 'page' }));
    const articles = (results.articles ?? []).map((article) => ({
      title: article.title,
      url: article.url,
      kind: 'article',
    }));
    return pages.concat(articles).slice(0, PAGES_LIMIT);
  };

  class R7Search extends HTMLElement {
    static get observedAttributes() {
      return ['open'];
    }

    connectedCallback() {
      if (this.input) return;

      this.context = this.dataset.context === 'menu' ? 'menu' : 'wide';
      this.limit = Number.parseInt(this.dataset.limit, 10) || 12;
      this.input = this.querySelector('[data-r7-input]');
      this.form = this.querySelector('form');
      this.suggest = this.querySelector('[data-r7-suggest]');
      this.results = this.querySelector('[data-r7-results]');
      this.scentGroup = this.querySelector('[data-r7-scents]');
      this.scentList = this.querySelector('[data-r7-scent-list]');
      this.productGroup = this.querySelector('[data-r7-products]');
      this.count = this.querySelector('[data-r7-count]');
      this.productList = this.querySelector('[data-r7-product-list]');
      this.viewAll = this.querySelector('[data-r7-all]');
      this.pageGroup = this.querySelector('[data-r7-pages]');
      this.pageList = this.querySelector('[data-r7-page-list]');
      this.none = this.querySelector('[data-r7-none]');
      this.fallback = this.querySelector('[data-r7-fallback]');
      this.status = this.querySelector('[data-r7-status]');
      this.rowTemplate = this.querySelector('template[data-r7-tpl="row"]');
      this.chips = new Map(
        [...this.querySelector('template[data-r7-tpl="chips"]').content.querySelectorAll('[data-r7-scent]')].map(
          (chip) => [chip.dataset.r7Scent, chip]
        )
      );

      this.query = '';
      this.found = null;
      this.pages = [];
      this.pagesQuery = null;
      this.pagesSettled = false;
      this.indexFailed = false;

      this.input.addEventListener('input', () => this.update());
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusin', () => this.load());
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-r7-close]')) {
          this.close('close');
        } else if (
          event.target.closest('[data-r7-suggest] a[href], [data-r7-results] a[href]') &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          this.dispatchClose('navigate');
        }
      });

      if (this.hasAttribute('open')) this.load();
    }

    disconnectedCallback() {
      clearTimeout(this.focusTimer);
      clearTimeout(this.announceTimer);
      this.abortPages();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === null) {
        clearTimeout(this.focusTimer);
      } else {
        this.load();
      }
    }

    get formatOrder() {
      try {
        const order = JSON.parse(this.dataset.formatOrder || '[]');
        return Array.isArray(order) ? order : [];
      } catch (error) {
        return [];
      }
    }

    focusInput() {
      this.load();
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => this.input?.focus({ preventScroll: true }), FOCUS_DELAY);
    }

    clear() {
      if (!this.input) return;
      this.input.value = '';
      this.update();
    }

    close(reason) {
      this.clear();
      this.dispatchClose(reason);
    }

    dispatchClose(reason) {
      this.dispatchEvent(new CustomEvent('r7:search-close', { bubbles: true, detail: { reason } }));
    }

    load() {
      if (this.prepared || this.loading) return;
      this.indexFailed = false;
      this.loading = loadIndex(this.dataset.indexUrl)
        .then((index) => {
          this.prepared = prepare(index, {
            formatOrder: this.formatOrder,
            normalize: window.R7?.normalize ?? normalize,
          });
          if (this.input) this.update();
        })
        .catch(() => {
          this.indexFailed = true;
        })
        .finally(() => {
          this.loading = null;
          if (this.input) this.render();
        });
    }

    update() {
      this.query = this.input.value.trim();
      const hasQuery = normalize(this.query) !== '';

      this.toggleAttribute('data-has-query', hasQuery);
      this.closest('[data-r7-search-scope]')
        ?.querySelectorAll('[data-r7-hide-on-query]')
        .forEach((element) => element.classList.toggle('r7-search-hidden', hasQuery));
      if (this.suggest) this.suggest.hidden = hasQuery;
      this.results.hidden = !hasQuery;

      if (!hasQuery) {
        this.abortPages();
        this.found = null;
        this.pages = [];
        this.pagesSettled = false;
        this.render();
        return;
      }

      /* A failed index load is retried on the next keystroke (load() resets indexFailed). */
      if (!this.prepared) this.load();
      this.found = this.prepared ? search(this.prepared, this.query) : null;
      this.requestPages(this.query);
      this.render();
    }

    render() {
      const found = this.found;
      const scents = found ? found.scents.filter((scent) => this.chips.has(scent.handle)) : [];
      const products = found ? found.products : [];
      const pages = this.pagesQuery === this.query ? this.pages : [];

      this.scentList.replaceChildren(...scents.map((scent) => this.chips.get(scent.handle).cloneNode(true)));
      this.scentGroup.hidden = scents.length === 0;

      this.productList.replaceChildren(
        ...products.slice(0, this.limit).map((product) =>
          this.row({
            url: product.url,
            name: product.name,
            upper: true,
            sub: product.format,
            price: product.price,
            image: product.image,
          })
        )
      );
      this.count.textContent = this.countLabel(products.length);
      this.productGroup.hidden = products.length === 0;

      const more = products.length > this.limit;
      this.viewAll.hidden = !more;
      if (more) {
        const params = new URLSearchParams({ q: this.query, 'options[prefix]': 'last' });
        this.viewAll.href = `${this.dataset.searchUrl}?${params}`;
        this.viewAll.textContent = `${this.dataset.viewAll} ${this.countLabel(products.length)} →`;
      }

      this.pageList.replaceChildren(
        ...pages.map((page) =>
          this.row({
            url: page.url,
            name: page.title,
            upper: false,
            sub: page.kind === 'article' ? this.dataset.articleLabel : this.dataset.pageLabel,
          })
        )
      );
      this.pageGroup.hidden = pages.length === 0;

      const total = scents.length + products.length + pages.length;
      const localSettled = found !== null || this.indexFailed;
      const settled = Boolean(this.query) && localSettled && this.pagesSettled && this.pagesQuery === this.query;
      const nothing = settled && total === 0;
      this.none.hidden = !nothing;
      if (nothing) this.none.textContent = this.dataset.none.replace('[query]', this.query);

      /* Index unavailable: the full search page still answers the query. */
      const fallback = settled && this.indexFailed;
      if (this.fallback) {
        this.fallback.hidden = !fallback;
        if (fallback) {
          const params = new URLSearchParams({ q: this.query, 'options[prefix]': 'last' });
          this.fallback.href = `${this.dataset.searchUrl}?${params}`;
          this.fallback.textContent = `${this.dataset.viewAll} →`;
        }
      }
      this.results.setAttribute('aria-busy', String(Boolean(this.query) && !settled));

      /* Announce what the count label shows: products only (E66, A01-5); otherwise the scent / page counts. */
      let message = '';
      if (nothing) message = this.none.textContent;
      else if (products.length > 0) message = this.countLabel(products.length);
      else if (scents.length > 0 || pages.length > 0) {
        message = [
          scents.length > 0 ? this.countLabel(scents.length, 'scents') : '',
          pages.length > 0 ? this.countLabel(pages.length, 'pages') : '',
        ]
          .filter(Boolean)
          .join(', ');
      }
      this.announce(message);
    }

    row({ url, name, upper, sub, price, image }) {
      const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
      row.href = url;
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.loading = 'lazy';
        img.decoding = 'async';
        row.querySelector('[data-r7-thumb]').append(img);
      }
      const nameElement = row.querySelector('[data-r7-name]');
      nameElement.textContent = name;
      nameElement.classList.toggle('r7-search__name--upper', upper);
      row.querySelector('[data-r7-sub]').textContent = sub ?? '';
      const priceElement = row.querySelector('[data-r7-price]');
      if (price) priceElement.textContent = price;
      else priceElement.remove();
      return row;
    }

    countLabel(count, kind = 'count') {
      const one = this.dataset[`${kind}One`] ?? this.dataset.countOne;
      const other = this.dataset[`${kind}Other`] ?? this.dataset.countOther;
      return count === 1 ? one : other.replace(COUNT_TOKEN, String(count));
    }

    announce(message) {
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        if (this.status.textContent !== message) this.status.textContent = message;
      }, ANNOUNCE_DELAY);
    }

    requestPages(query) {
      if (query === this.pagesQuery) return;
      this.abortPages();
      this.pagesQuery = query;
      this.pages = [];
      this.pagesSettled = false;

      const controller = new AbortController();
      this.pagesController = controller;
      this.pagesTimer = setTimeout(() => {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'page,article',
          'resources[limit]': String(PAGES_LIMIT),
        });
        fetch(`${this.dataset.suggestUrl}?${params}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Search suggest request failed: ${response.status}`);
            return response.json();
          })
          .then(pagesFrom, () => [])
          .then((pages) => {
            if (controller.signal.aborted) return;
            this.pages = pages;
            this.pagesSettled = true;
            this.render();
          });
      }, PAGES_DELAY);
    }

    abortPages() {
      clearTimeout(this.pagesTimer);
      this.pagesController?.abort();
      this.pagesController = null;
      this.pagesQuery = null;
    }

    navItems() {
      return [...this.querySelectorAll('[data-r7-suggest] a[href], [data-r7-results] a[href]')].filter(
        (element) => element.getClientRects().length > 0
      );
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.context !== 'wide') return;
        event.preventDefault();
        this.close('escape');
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const items = this.navItems();
      if (event.target === this.input) {
        if (event.key === 'ArrowDown' && items.length) {
          event.preventDefault();
          items[0].focus();
        }
        return;
      }

      const position = items.indexOf(document.activeElement);
      if (position === -1) return;
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        items[Math.min(position + 1, items.length - 1)].focus();
      } else if (position === 0) {
        this.input.focus();
      } else {
        items[position - 1].focus();
      }
    }
  }

  customElements.define('r7-search', R7Search);
})();
